import type React from 'react';
import {Box, LinearProgress, Stack, Typography} from '@mui/material';
import {ClickToEdit} from '~/components/ClickToEdit/ClickToEdit';
import {budgetMetrics} from '../../domain/budget';
import {parseMoney} from '../../domain/planText';
import type {SpendAnalysis} from '../../types/categoryProfileTypes';

type Props = {
    ytd: SpendAnalysis['yearToDate'];
    budget: {amount: number; year: number} | null;
    canEdit: boolean;
    money: (v: number) => string;
    onSetBudget: (budget: {amount: number; year: number} | null) => void;
};

/**
 * Budget vs actual (spec 4.4): Budget · Actual YTD · Run rate vs budget, and how much of the budget is used against
 * how much of the year has passed. Mithra has no budget source, so the team enters the annual budget here.
 */
export const BudgetVsActual: React.FC<Props> = ({ytd, budget, canEdit, money, onSetBudget}) => {
    const sameYear = budget?.year === ytd.year;
    const m = budget && sameYear ? budgetMetrics(budget.amount, ytd.actual, ytd.months) : null;
    const editBudget = (
        <ClickToEdit text={budget && sameYear ? money(budget.amount) : ''} readOnly={!canEdit}
                     placeholder={canEdit ? 'Set budget' : 'Not set'}
                     typographyProps={{variant: 'h6', sx: {fontWeight: 600}}}
                     onSave={v => {
                         const trimmed = v.trim();
                         if (!trimmed) return onSetBudget(null);
                         const amount = parseMoney(trimmed);
                         if (amount !== null) onSetBudget({amount, year: ytd.year});
                     }}/>
    );
    const tiles = [
        {label: `Budget FY${ytd.year}`, value: editBudget},
        {label: 'Actual YTD', value: <Typography variant="h6" sx={{fontWeight: 600}}>{money(ytd.actual)}</Typography>},
        {label: 'Run rate vs budget', value: m ? (
            <Typography variant="h6" sx={{fontWeight: 600}} color={m.runRateVsBudgetPct > 0 ? 'error.main' : 'success.main'}>
                {`${m.runRateVsBudgetPct > 0 ? '+' : ''}${m.runRateVsBudgetPct.toFixed(1)}%`}
            </Typography>
        ) : <Typography variant="h6" color="text.disabled">—</Typography>},
    ];

    return (
        <Stack gap={1.5}>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid', borderColor: 'divider', borderRadius: 1.5}}>
                {tiles.map((t, i) => (
                    <Box key={t.label} sx={{p: 1.5, borderLeft: i ? '1px solid' : 'none', borderColor: 'divider', minWidth: 0}}>
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>{t.label}</Typography>
                        {t.value}
                    </Box>
                ))}
            </Box>
            {m ? (
                <>
                    <Box sx={{position: 'relative'}}>
                        <LinearProgress variant="determinate" value={Math.min(100, m.usedPct)} aria-label="Budget used"
                                        color={m.usedPct > m.elapsedPct ? 'warning' : 'primary'} sx={{height: 8, borderRadius: 4}}/>
                        <Box title="Share of the year elapsed" sx={{position: 'absolute', top: -3, bottom: -3, left: `${m.elapsedPct}%`,
                            width: 2, bgcolor: 'text.primary'}}/>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {`${m.usedPct.toFixed(0)}% of budget used, ${m.elapsedPct.toFixed(0)}% of the year elapsed (${ytd.months} of 12 months). `}
                        {`Projected full year: ${money(m.runRate)}.`}
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {budget && !sameYear
                        ? `The budget on file is for ${budget.year}; set the ${ytd.year} budget to compare.`
                        : canEdit ? 'Click the budget to enter this year\'s budget for the category.' : 'No budget entered for this year.'}
                </Typography>
            )}
        </Stack>
    );
};
